import {
    SpaceBetween,
    BreadcrumbGroup,
    Flashbar,
    Header,
    Container,
    Form,
    FormField,
    Input,
    Button,
    Multiselect,
    Alert,
    Box,
    ColumnLayout,
} from '@amzn/awsui-components-console';
import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import InputWithValidation from '../../bot-accounts/common/InputWithValidation';
import { configureIAMRole, getIAMRole } from '../apis';
import {
    STATUS_INACTIVE,
    CallerArnsEditor,
    apiErrorMessage,
    apiErrorStatus,
    isOffboardInterrupted,
    loadPrivateGroupOptions,
    preselectSubscribedGroups,
    renderStatus,
    roleStatus,
    toSubscribedGroups,
    validateCallerArns,
} from '../utils';
import { displayErrorContent, isAdmin, isValidInput, LoadingSpinner } from '../../../lib/utils';
import { CLIENT_ERROR, SERVER_ERROR } from '../../../lib/constants';

export const LAST_CALLER_HINT =
    'An IAM role needs at least one trusted caller. To remove access entirely, offboard the role instead.';

/**
 * The identityId route parameter, URL-decoded. A role ARN contains ':' and '/', so the list page
 * encodes it into the path.
 * @param {string} value - the raw route parameter
 */
function decodeIdentityId(value) {
    try {
        return decodeURIComponent(value || '');
    } catch (e) {
        return value || '';
    }
}

/**
 * A labelled read-only value.
 */
function ReadOnlyField({ label, children }) {
    return (
        <div>
            <Box variant="awsui-key-label">{label}</Box>
            <div>{children}</div>
        </div>
    );
}

/**
 * The role without a form, for an owner looking at a role a Device Farm admin disabled.
 * @param {object} props
 * @param {object} props.role - the role as read from GET /v1/role?identityId=
 */
export function ReadOnlyRole({ role }) {
    const subscribedGroups = role.subscribedGroups || {};
    const groupIds = Object.keys(subscribedGroups)
        .sort()
        .flatMap((region) => subscribedGroups[region].map((groupId) => `${region}: ${groupId}`));
    return (
        <SpaceBetween direction="vertical" size="m">
            {isOffboardInterrupted(role) ? (
                <Alert type="warning" header="Offboarding stopped part-way">
                    This IAM role can no longer get tokens. Retry the offboard from the IAM roles page to finish
                    removing it.
                </Alert>
            ) : (
                <Alert type="info" header="Disabled by a Device Farm admin">
                    This IAM role is disabled: its callers cannot get tokens, and it cannot be configured. Contact
                    @ds2-device-farm-dev to enable it.
                </Alert>
            )}
            <Container header={<Header variant="h2">IAM role details: {role.team}</Header>}>
                <ColumnLayout columns={2} variant="text-grid">
                    <ReadOnlyField label="Team">{role.team}</ReadOnlyField>
                    <ReadOnlyField label="Device Farm role ARN">{role.identity_id}</ReadOnlyField>
                    <ReadOnlyField label="Status">{renderStatus(role)}</ReadOnlyField>
                    <ReadOnlyField label="Owner group">{role.ownerGroup || '-'}</ReadOnlyField>
                    <ReadOnlyField label="Description">{role.description || '-'}</ReadOnlyField>
                    <ReadOnlyField label="Trusted callers">
                        {(role.caller_role_arns || []).map((arn) => (
                            <Box key={arn}>{arn}</Box>
                        ))}
                    </ReadOnlyField>
                    <ReadOnlyField label="Subscribed private groups">
                        {groupIds.length > 0 ? groupIds.map((groupId) => <Box key={groupId}>{groupId}</Box>) : '-'}
                    </ReadOnlyField>
                </ColumnLayout>
            </Container>
            <Box float="right">
                <Button href="#/api-auth/iam-roles">Back</Button>
            </Box>
        </SpaceBetween>
    );
}

export default function ConfigureIAMRole() {
    const history = useHistory();
    const identityId = decodeIdentityId(useParams().identityId);
    const [role, setRole] = React.useState(null);
    const [roleStatusType, setRoleStatusType] = React.useState('loading');
    const [isDdfAdmin, setIsDdfAdmin] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState([]);
    const [description, setDescription] = React.useState('');
    const [ownerGroup, setOwnerGroup] = React.useState('');
    const [ownerGroupError, setOwnerGroupError] = React.useState('');
    const [callerArns, setCallerArns] = React.useState([]);
    const [callerArnErrors, setCallerArnErrors] = React.useState([]);
    const [groupsOptions, setGroupsOptions] = React.useState([]);
    const [groupsSelectedOptions, setGroupsSelectedOptions] = React.useState([]);
    const [groupsStatus, setGroupsStatus] = React.useState('pending');
    const [confirmLoading, setConfirmLoading] = React.useState(false);

    /**
     * validate input
     */
    const validateInput = () => {
        const nextCallerArnErrors = validateCallerArns(callerArns);
        const nextOwnerGroupError = isValidInput(ownerGroup) ? '' : 'Owner group is required and should not be empty.';
        setCallerArnErrors(nextCallerArnErrors);
        setOwnerGroupError(nextOwnerGroupError);
        return nextCallerArnErrors.every((error) => !error) && !nextOwnerGroupError;
    };

    /**
     * click confirm button to send the full desired state
     */
    const onClick = async () => {
        if (groupsStatus === 'loading') {
            displayErrorContent(CLIENT_ERROR, 'Loading subscribed private groups, please wait.', setErrorMessage);
            return;
        }
        // configure replaces the role's private groups, so it must not run without knowing them
        if (groupsStatus === 'error') {
            displayErrorContent(
                SERVER_ERROR,
                'Loading subscribed private groups failed, try again later or contact our dev team @ds2-device-farm-dev for more details.',
                setErrorMessage
            );
            return;
        }
        setErrorMessage([]);
        if (!validateInput()) {
            return;
        }

        setConfirmLoading(true);
        let success = false;
        try {
            await configureIAMRole(
                identityId,
                callerArns.map((arn) => arn.trim()),
                ownerGroup.trim(),
                description,
                toSubscribedGroups(groupsSelectedOptions)
            );
            success = true;
        } catch (err) {
            console.error('Fail to configure the IAM role with error', err);
            const status = apiErrorStatus(err);
            if ([400, 403, 404, 409].includes(status)) {
                const data = err.response.data || {};
                displayErrorContent(
                    data.errorException || CLIENT_ERROR,
                    apiErrorMessage(err) || 'The request was rejected.',
                    setErrorMessage
                );
            } else {
                displayErrorContent(
                    SERVER_ERROR,
                    'Fail to configure this IAM role, try again later or contact our dev team @ds2-device-farm-dev for more details.',
                    setErrorMessage
                );
            }
        }
        setConfirmLoading(false);
        if (success) {
            history.push('/api-auth/iam-roles');
        }
    };

    useEffect(() => {
        if (!identityId) {
            history.push('/api-auth/iam-roles');
            return undefined;
        }
        let active = true;
        (async function () {
            let loaded;
            try {
                const [response, adminResponse] = await Promise.all([
                    getIAMRole(identityId),
                    // a failed check means not an admin: the backend decides regardless
                    isAdmin().catch(() => ({ status: false })),
                ]);
                loaded = response['response'];
                if (!active) {
                    return;
                }
                setIsDdfAdmin(!!(adminResponse && adminResponse.status));
                setRole(loaded);
                setDescription(loaded.description || '');
                setOwnerGroup(loaded.ownerGroup || '');
                setCallerArns(
                    loaded.caller_role_arns && loaded.caller_role_arns.length ? loaded.caller_role_arns : ['']
                );
                setRoleStatusType('finished');
                if (roleStatus(loaded) === STATUS_INACTIVE && !(adminResponse && adminResponse.status)) {
                    // read-only view: it lists the role's own groups and needs no options
                    return;
                }
            } catch (err) {
                console.error('Fail to load the IAM role with error', err);
                if (active) {
                    setRoleStatusType('error');
                    displayErrorContent(
                        apiErrorStatus(err) === 404 ? CLIENT_ERROR : SERVER_ERROR,
                        apiErrorMessage(err) ||
                            'Fail to load this IAM role, try again later or contact our dev team @ds2-device-farm-dev for more details.',
                        setErrorMessage
                    );
                }
                return;
            }

            setGroupsStatus('loading');
            try {
                const options = await loadPrivateGroupOptions();
                if (!active) {
                    return;
                }
                const preselected = preselectSubscribedGroups(options, loaded.subscribedGroups);
                setGroupsOptions(preselected.options);
                setGroupsSelectedOptions(preselected.selectedOptions);
                setGroupsStatus('finished');
            } catch (e) {
                console.error('Fail to load private group(s) with error', e);
                if (active) {
                    setGroupsStatus('error');
                }
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    const breadcrumbs = (
        <BreadcrumbGroup
            items={[
                { text: 'Amazon Device Farm', href: '#' },
                { text: 'IAM roles', href: '#/api-auth/iam-roles' },
                { text: 'Configure', href: `#/api-auth/iam-roles/configure/${encodeURIComponent(identityId)}` },
            ]}
            ariaLabel="Breadcrumbs"
        />
    );

    if (roleStatusType === 'loading') {
        return (
            <SpaceBetween size="s">
                {breadcrumbs}
                <LoadingSpinner />
            </SpaceBetween>
        );
    }
    if (roleStatusType === 'error') {
        return (
            <SpaceBetween size="s">
                {breadcrumbs}
                <Flashbar items={errorMessage} />
                <Box float="right">
                    <Button href="#/api-auth/iam-roles">Back</Button>
                </Box>
            </SpaceBetween>
        );
    }
    const inactive = roleStatus(role) === STATUS_INACTIVE;
    if (inactive && !isDdfAdmin) {
        return (
            <SpaceBetween size="s">
                {breadcrumbs}
                <ReadOnlyRole role={role} />
            </SpaceBetween>
        );
    }

    return (
        <SpaceBetween size="s">
            {breadcrumbs}
            <form onSubmit={(e) => e.preventDefault()}>
                <Form
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button formAction="none" href="#/api-auth/iam-roles">
                                Back
                            </Button>
                            <Button formAction="none" loading={confirmLoading} onClick={onClick} variant="primary">
                                Confirm
                            </Button>
                        </SpaceBetween>
                    }
                >
                    <SpaceBetween direction="vertical" size="m">
                        {inactive && (
                            <Alert type="info" header="This IAM role is disabled">
                                Configuring it does not enable it. Use Enable on the IAM roles page.
                            </Alert>
                        )}
                        <Container header={<Header variant="h2">IAM role details: {role.team}</Header>}>
                            <SpaceBetween direction="vertical" size="m">
                                <FormField label="Team">
                                    <Input value={role.team} disabled />
                                </FormField>
                                <FormField label="Device Farm role ARN">
                                    <Input value={role.identity_id} disabled />
                                </FormField>
                                <FormField label="Status">{renderStatus(role)}</FormField>
                                <FormField
                                    label={
                                        <span>
                                            Description <i>- optional</i>{' '}
                                        </span>
                                    }
                                >
                                    <Input
                                        placeholder="Enter optional description"
                                        value={description}
                                        onChange={(event) => {
                                            setDescription(event.detail.value);
                                        }}
                                    />
                                </FormField>
                                <InputWithValidation
                                    label="Owner group"
                                    type="text"
                                    description="LDAP group that owns this IAM role. You must be a member of a new owner group."
                                    errorText={ownerGroupError}
                                    placeholder="Enter owner group"
                                    value={ownerGroup}
                                    setValue={setOwnerGroup}
                                    setError={setOwnerGroupError}
                                ></InputWithValidation>
                                <CallerArnsEditor
                                    arns={callerArns}
                                    onChange={(arns) => {
                                        setCallerArns(arns);
                                        setCallerArnErrors([]);
                                    }}
                                    itemErrors={callerArnErrors}
                                    lastItemHint={LAST_CALLER_HINT}
                                />
                                <FormField
                                    label={
                                        <span>
                                            Subscribed private groups <i>- optional</i>{' '}
                                        </span>
                                    }
                                    description="The Common group is always kept. This step may take few seconds when multiple private groups change."
                                >
                                    <Multiselect
                                        selectedOptions={groupsSelectedOptions}
                                        expandToViewport
                                        placeholder="Select private groups to subscribe"
                                        loadingText="Loading groups"
                                        errorText="Error fetching groups"
                                        finishedText="End of all results"
                                        empty="No group found"
                                        filteringType="auto"
                                        virtualScroll={true}
                                        statusType={groupsStatus}
                                        options={groupsOptions}
                                        onChange={({ detail }) => {
                                            setGroupsSelectedOptions(detail.selectedOptions);
                                        }}
                                    />
                                </FormField>
                            </SpaceBetween>
                        </Container>
                        <Flashbar items={errorMessage} />
                    </SpaceBetween>
                </Form>
            </form>
        </SpaceBetween>
    );
}
